import React, { FC, useEffect, useMemo, useState } from 'react'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { format } from 'date-fns'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Button } from '@/atoms/Button'
import { FlexColumn, ResponsiveFlexRow } from '@/atoms/FlexContainers'
import { ArrowRightIcon } from '@/atoms/Icons/ArrowRightIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { HeadingMD, ParagraphMD, ParagraphSM, Text } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { Banner } from '@/molecules/Banner'
import { TheaterBuyoutRequestMutation, TheaterBuyoutRequestMutationVariables } from '@/types/codegen-federation'
import { useSafeAnalytics } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { BuyoutProjectDataType } from '@/views/Buyout/constants'

type BuyoutConfirmationViewProps = {
  theatricalSlug: string
  buyoutData: BuyoutProjectDataType
  locale: string
}

export const THEATER_BUYOUT_MUTATION = gql`
  mutation theaterBuyoutRequest($input: TheaterBuyoutRequestInput!) {
    theaterBuyoutRequest(input: $input) {
      success
    }
  }
`

type Inputs = {
  email: string
  groupSize: number
  name: string
  phone: string
  preferredDate: string
  preferredTime: string
  theaterName: string
  zipCode: string
  audioLanguage: string
  subtitleLanguage: string
}

export function formatDate(dateString: string) {
  return format(new Date(dateString), 'MMMM do')
}

function formatDateForLimits(dateString: string) {
  // Convert the date to an ISO string and extract the date part
  const isoString = new Date(dateString)?.toISOString()
  return isoString?.split('T')?.[0]
}

const BuyoutView: FC<BuyoutConfirmationViewProps> = ({ theatricalSlug, buyoutData, locale }) => {
  const { t } = useTranslate('tickets')
  const { track } = useSafeAnalytics()
  const { push, query } = useSafeRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitBuyout] = useMutation<TheaterBuyoutRequestMutation, TheaterBuyoutRequestMutationVariables>(
    THEATER_BUYOUT_MUTATION,
    {
      errorPolicy: 'all',
    },
  )

  const theaterName = query?.theaterName
  const theaterZip = query?.theaterZipCode
  const mobileImage = buyoutData?.mobileImageUrl?.[locale] ?? buyoutData?.mobileImageUrl?.default
  const desktopImage = buyoutData?.desktopImageUrl?.[locale] ?? buyoutData?.desktopImageUrl?.default
  const projectName = buyoutData?.projectName
  const minDate = formatDate(buyoutData?.dateRange?.min)
  const maxDate = formatDate(buyoutData?.dateRange?.max)
  const linkText = buyoutData?.link

  const languageOptions = useMemo(() => {
    return buyoutData?.laguages?.map((language) => {
      return { value: language.code, text: language.name }
    })
  }, [buyoutData?.laguages])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<Inputs>()

  useEffect(() => {
    if (theaterName) {
      setValue('theaterName', theaterName as string)
    }

    if (theaterZip) {
      setValue('zipCode', theaterZip as string)
    }
  }, [setValue, theaterName, theaterZip])

  const onSubmit: SubmitHandler<Inputs> = async (formData) => {
    try {
      setIsSubmitting(true)
      const input = { ...formData, theatricalSlug }
      const { data } = await submitBuyout({
        variables: {
          input,
        },
      })
      if (data?.theaterBuyoutRequest?.success) {
        track('Buyout Form Submitted', input)
        return push(`/buyout/${theatricalSlug}/confirmation`)
      }
      setIsSubmitting(false)
    } catch (error) {
      setIsSubmitting(false)
      logger().error('Buy out mutation failed', { error, formData })
    }
  }

  return (
    <div className="bg-white pt-16">
      <div className="mx-auto max-w-[1440px]">
        <Banner
          wrapperClassName="border-none md:hidden"
          imageClassName="rounded-none m-0"
          bannerClassName="rounded-none relative m-0"
          banner={{ backgroundCloudinaryPath: mobileImage, id: 'v1713391871' }}
        />
        <Banner
          wrapperClassName="hidden md:block border-none xl:mt-6"
          imageClassName="rounded-none xl:rounded-2xl"
          bannerClassName="xl:rounded-2xl"
          banner={{ backgroundCloudinaryPath: desktopImage, id: 'v1713391871' }}
        />
        <ResponsiveFlexRow className="!items-start justify-center pb-10 lg:gap-20 lg:px-4 lg:py-16" breakPoint="lg">
          <FlexColumn className="gap-3 bg-core-gray-900 px-4 py-8 text-white lg:max-w-[470px] lg:rounded-2xl lg:px-8 lg:py-10">
            <HeadingMD weight="bold">
              {t('buyoutTitleV4', 'Schedule a private showing of {{projectName}} before anyone else.', { projectName })}
            </HeadingMD>
            <div className="my-3 h-[2px] w-full rounded-full bg-warm-03" />
            <ParagraphMD weight="normal">
              <Translate
                i18nKey="buyoutAvailableDatesDescriptionV5"
                t={t}
                values={{ projectName, minDate, maxDate, linkText }}
              >
                Private showing dates of {{ projectName }} are available as early as {{ minDate }}, 2024. If you&apos;re
                interested, submit your information and we&apos;ll contact you shortly to arrange your early showing.
                <br />
                <br />
                Don&apos;t want to buyout an entire theater? Use our{' '}
                <span className="font-bold text-warm-03">Group Discount</span> option to purchase directly from{' '}
                <a className="underline" href={`https://angel.com/tickets/${theatricalSlug}`}>
                  {{ linkText }}
                </a>
                . Only available with Angel Partner Theaters at Angel.com.
              </Translate>
            </ParagraphMD>
          </FlexColumn>
          <FlexColumn className="mt-10 w-full gap-4 px-4 md:gap-6 lg:mt-0 lg:max-w-[526px] lg:px-0 lg:pb-20">
            <Text className="photon-title-md lg:photon-title-lg !font-bold">
              {t('privateShowingForm', 'Private Showing Form')}
            </Text>
            <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
              <BuyoutInput
                label={t('fullNameLabel', 'Full Name')}
                type="text"
                placeholder={t('fullNamePlaceholder', 'ex: John Smith')}
                {...register('name', { required: true })}
                shouldShowError={!!errors?.name}
              />
              <BuyoutInput
                label={t('emailLabel', 'Email')}
                type="email"
                placeholder={t('emailPlaceholder', 'ex: johnsmith@example.com')}
                {...register('email', { required: true })}
                shouldShowError={!!errors?.email}
              />

              <div className="flex grid-cols-2 flex-col gap-4 md:grid">
                <BuyoutInput
                  label={t('cellPhone', 'Cell Phone')}
                  type="tel"
                  placeholder={t('cellPhone', 'Cell Phone')}
                  {...register('phone', { required: true })}
                  shouldShowError={!!errors?.phone}
                />
                <BuyoutInput
                  label={t('theaterNameLabel', 'Theater Name')}
                  type="text"
                  placeholder={t('theaterNameLabel', 'Theater Name')}
                  {...register('theaterName', { required: true })}
                  shouldShowError={!!errors?.theaterName}
                />
                <BuyoutInput
                  label={t('zipLabel', 'Theater Zip Code')}
                  type="text"
                  placeholder={t('zipLabel', 'Theater Zip Code')}
                  {...register('zipCode', { required: true })}
                  shouldShowError={!!errors?.zipCode}
                />

                <BuyoutInput
                  label={`${t('dateLabel', 'Preferred Date')}`}
                  type="date"
                  min={formatDateForLimits(buyoutData?.dateRange?.min)}
                  placeholder={t('dateLabel', 'Preferred Date')}
                  {...register('preferredDate', {
                    required: true,
                  })}
                  shouldShowError={!!errors?.preferredDate}
                />
                <BuyoutInput
                  label={t('timeLabel', 'Preferred Time')}
                  type="time"
                  placeholder={t('timeLabel', 'Preferred Time')}
                  {...register('preferredTime', { required: true })}
                  shouldShowError={!!errors?.preferredTime}
                />
                <BuyoutInput
                  label={t('groupLabel', 'Group Size')}
                  type="number"
                  min={40}
                  placeholder={t('numberOfGroup', '# of group')}
                  {...register('groupSize', { required: true, valueAsNumber: true })}
                  shouldShowError={!!errors?.groupSize}
                />
                {theatricalSlug === slugs.sight && languageOptions && (
                  <BuyoutSelect
                    label={t('audioLanguage', 'Audio Language')}
                    options={languageOptions}
                    {...register('audioLanguage')}
                  />
                )}
                {theatricalSlug === slugs.sight && languageOptions && (
                  <BuyoutSelect
                    label={t('subtitleLanguage', 'Subtitle Language')}
                    options={[{ value: '', text: '' }, ...languageOptions]}
                    {...register('subtitleLanguage')}
                  />
                )}
              </div>
              <Button
                variant="black"
                type="submit"
                className="py mt-2 flex min-w-[225px] items-center justify-center gap-1 px-6 py-3.5 md:mr-auto"
              >
                {isSubmitting && <LoadingSpinnerIcon size={18} color="white" />}
                {t('submitForm', 'Submit form')}
                <ArrowRightIcon size={22} />
              </Button>
            </form>
          </FlexColumn>
        </ResponsiveFlexRow>
      </div>
    </div>
  )
}

interface BuyoutSelectProps extends React.ComponentPropsWithRef<'select'> {
  label: string
  options: { value: string; text: string }[]
  shouldShowError?: boolean
}
const BuyoutSelect = function BuyoutInput(props: BuyoutSelectProps) {
  const { t } = useTranslate('tickets')
  const { label, shouldShowError, options, ...inputProps } = props

  return (
    <FlexColumn className="gap-1">
      <label htmlFor={props.id} className="text-sm text-core-gray-700">
        {label}
        <select
          {...inputProps}
          className="mt-1 w-full rounded-[10px] border-[1.5px] border-core-gray-300 p-4 text-sm placeholder:!text-core-gray-500"
        >
          {options?.map((option) => {
            return (
              <option key={option.value} value={option.value}>
                {option.text}
              </option>
            )
          })}
        </select>
      </label>
      {shouldShowError && (
        <ParagraphSM color="red-500">{t('requiredInputField', 'This field is required')}</ParagraphSM>
      )}
    </FlexColumn>
  )
}

interface BuyoutInputProps extends React.ComponentPropsWithRef<'input'> {
  label: string
  shouldShowError?: boolean
}
const BuyoutInput = function BuyoutInput(props: BuyoutInputProps) {
  const { t } = useTranslate('tickets')
  const { label, shouldShowError, ...inputProps } = props

  return (
    <FlexColumn className="gap-1">
      <label htmlFor={props.id} className="text-sm text-core-gray-700">
        {label}
        <input
          {...inputProps}
          className="mt-1 w-full rounded-[10px] border-[1.5px] border-core-gray-300 p-4 text-sm placeholder:!text-core-gray-500"
        />
      </label>
      {shouldShowError && (
        <ParagraphSM color="red-500">{t('requiredInputField', 'This field is required')}</ParagraphSM>
      )}
    </FlexColumn>
  )
}

export default BuyoutView
